import { useTranslation } from "react-i18next";
import TableHeaderSortingButton from "../../../../../shared/components/button/TableHeaderSortingButton";
import {
  formatToEurosCurrency,
  formatToTwoDecimalPlaces,
} from "../../../../../shared/lib/utils";
import { DataTable } from "../../../../../shared/components/data-table/DataTable";
import TacticLineCurrentStatusBadge from "./TacticLineCurrentStatusBadge";
import TacticLineOverwrittenBadge from "./TacticLineOverwrittenBadge";

const TacticLinesPerformanceTable = ({ data }) => {
  const { t } = useTranslation();

  const columns = [
    {
      accessorKey: "platform",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.platform")}
          />
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: "tacticLineName",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.name")}
          />
        );
      },
      cell: ({ row }) => {
        const data = row.original;
        return (
          <div>
            {data.tacticLineName}{" "}
            <TacticLineOverwrittenBadge isOverwritten={data.overwritten} />
          </div>
        );
      },
      enableHiding: false,
    },
    {
      accessorKey: "tacticLineCurrentStatus",
      header: t("tacticLinesEfficiency.table.header.currentStatus"),
      cell: ({ row }) => (
        <TacticLineCurrentStatusBadge
          value={row.getValue("tacticLineCurrentStatus")}
        />
      ),
      enableHiding: false,
    },
    {
      accessorKey: "investment",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.investment")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToEurosCurrency(row.getValue("investment"))}
        </div>
      ),
    },
    {
      accessorKey: "impressions",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.impressions")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">{row.getValue("impressions")}</div>
      ),
      show: false,
    },
    {
      accessorKey: "cpm",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.cpm")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToTwoDecimalPlaces(row.getValue("cpm"))}
        </div>
      ),
    },
    {
      accessorKey: "clicks",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.clicks")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToTwoDecimalPlaces(row.getValue("clicks"))}
        </div>
      ),
      show: false,
    },
    {
      accessorKey: "cpc",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.cpc")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToTwoDecimalPlaces(row.getValue("cpc"))}
        </div>
      ),
    },
    {
      accessorKey: "ctr",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.ctr")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToTwoDecimalPlaces(row.getValue("ctr"))} %
        </div>
      ),
    },
    {
      accessorKey: "sessions",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.sessions")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">{row.getValue("sessions")}</div>
      ),
      show: false,
    },
    {
      accessorKey: "bouncesRatio",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.bouncesRatio")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToTwoDecimalPlaces(row.getValue("bouncesRatio"))} %
        </div>
      ),
      show: false,
    },
    {
      accessorKey: "newUsers",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.newUsers")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">{row.getValue("newUsers")}</div>
      ),
      show: false,
    },
    {
      accessorKey: "conversions",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.conversions")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToTwoDecimalPlaces(row.getValue("conversions"))}
        </div>
      ),
    },
    {
      accessorKey: "revenue",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.revenue")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToEurosCurrency(row.getValue("revenue"))}
        </div>
      ),
    },
    {
      accessorKey: "roas",
      header: ({ column }) => {
        return (
          <TableHeaderSortingButton
            column={column}
            headerText={t("tacticLinesEfficiency.table.header.roas")}
          />
        );
      },
      cell: ({ row }) => (
        <div className="text-center">
          {formatToTwoDecimalPlaces(row.getValue("roas"))}
        </div>
      ),
    },
  ];

  return (
    <DataTable columns={columns} data={data} enableColumnVisibility={true} />
  );
};

export default TacticLinesPerformanceTable;
